import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  paper: {
    "& .MuiDialog-paper": {
      width: 400,
      maxWidth: "95vw",
    },
  },
  header: {
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  content: {
    padding: "24px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 20,
  },
  previewBox: {
    width: 160,
    height: 160,
    borderRadius: "50%",
    border: `2px dashed ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
    position: "relative",
    "&:hover $hoverOverlay": {
      opacity: 1,
    },
  },
  previewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  hoverOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.2s",
    borderRadius: "50%",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: 10,
  },
  removeBtn: {
    color: "#dc2626",
    borderColor: "#dc2626",
    "&:hover": { backgroundColor: "#fee2e2", borderColor: "#dc2626" },
  },
  hint: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

const GroupImageModal = ({ open, onClose, groupJid, whatsappId, groupName }) => {
  const classes = useStyles();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Selecione uma imagem (JPG, PNG ou WEBP)");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo: 5MB");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
    // reset input so re-selecting same file triggers onChange
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("whatsappId", String(whatsappId));

      await api.put(`/groups/${encodeURIComponent(groupJid)}/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Foto do grupo atualizada!");
      setPreview(null);
      setFile(null);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao atualizar foto");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remover a foto do grupo?")) return;
    setRemoving(true);
    try {
      await api.delete(`/groups/${encodeURIComponent(groupJid)}/image`, {
        data: { whatsappId },
      });
      toast.success("Foto removida!");
      setPreview(null);
      setFile(null);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao remover foto");
    } finally {
      setRemoving(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    setFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} className={classes.paper} maxWidth={false}>
      <Box className={classes.header}>
        <Box>
          <Typography style={{ fontWeight: 600, fontSize: "0.95rem" }}>Foto do Grupo</Typography>
          <Typography variant="caption" color="textSecondary">{groupName}</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent className={classes.content}>
        {/* Preview circular clicável */}
        <Box
          className={classes.previewBox}
          onClick={() => fileRef.current?.click()}
        >
          {preview ? (
            <img src={preview} alt="preview" className={classes.previewImg} />
          ) : (
            <PhotoCameraIcon style={{ fontSize: 48, color: "#9ca3af" }} />
          )}
          <Box className={classes.hoverOverlay}>
            <PhotoCameraIcon style={{ color: "#fff", fontSize: 32 }} />
          </Box>
        </Box>

        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <Typography className={classes.hint}>
          Clique na imagem para selecionar uma foto (JPG, PNG ou WEBP, máx. 5MB)
        </Typography>

        <Box className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={file ? handleUpload : () => fileRef.current?.click()}
            disabled={uploading}
            startIcon={
              uploading
                ? <CircularProgress size={16} color="inherit" />
                : <CloudUploadIcon />
            }
          >
            {file ? "Enviar foto" : "Selecionar foto"}
          </Button>

          {file && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => { setPreview(null); setFile(null); }}
              disabled={uploading}
            >
              Cancelar seleção
            </Button>
          )}

          <Button
            variant="outlined"
            fullWidth
            className={classes.removeBtn}
            onClick={handleRemove}
            disabled={removing || uploading}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            Remover foto atual
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GroupImageModal;
